// LPS domain API client - recordings, reports, chat, org settings.
import axios from 'axios';

// Relative base: the CRA dev-server proxy (package.json "proxy") forwards to the
// backend, so the same origin works locally, on EC2, and behind any future ALB.
const client = axios.create({ baseURL: '/api/lps', timeout: 120000 });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const lpsService = {
  listRecordings: () => client.get('/recordings/').then((r) => r.data.items),
  getRecording: (id) => client.get(`/recordings/${id}`).then((r) => r.data),
  uploadRecording: (file, fields) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(fields || {}).forEach(([k, v]) => fd.append(k, v));
    return client.post('/recordings/upload', fd).then((r) => r.data);
  },
  reprocess: (id) => client.post(`/recordings/${id}/reprocess`).then((r) => r.data),
  fetchAudio: (id) =>
    client.get(`/recordings/${id}/audio`, { responseType: 'blob' })
      .then((r) => URL.createObjectURL(r.data)),

  getReport: (recordingId) => client.get(`/reports/${recordingId}`).then((r) => r.data),
  getLongitudinal: (subjectId) =>
    client.get('/reports/analytics/longitudinal', { params: { subject_id: subjectId } })
      .then((r) => r.data),
  saveSections: (recordingId, sections) =>
    client.put(`/reports/${recordingId}/sections`, { sections }).then((r) => r.data),
  finalize: (recordingId) => client.post(`/reports/${recordingId}/finalize`).then((r) => r.data),

  ask: (question, recordingId) =>
    client.post('/chat/ask', { question, recording_id: recordingId || null }).then((r) => r.data),

  getOrg: () => client.get('/org/').then((r) => r.data),
  updateOrg: (update) => client.put('/org/', update).then((r) => r.data),
  auditLog: () => client.get('/org/audit').then((r) => r.data.items),

  // persona-first workspace (subject files, practice mirror, vertical nouns)
  getVertical: () => client.get('/org/vertical').then((r) => r.data),
  listSubjects: () => client.get('/subjects').then((r) => r.data.items),
  getSubject: (subjectId) => client.get(`/subjects/${encodeURIComponent(subjectId)}`).then((r) => r.data),
  practiceSummary: () => client.get('/practice/summary').then((r) => r.data),
};

export default lpsService;
